// Centralized Agents Registry (single source of truth)
// Responsibilities:
// - Fetch and cache the agent list with a TTL keyed by userEmail+orgId+role
// - Fetch and cache per-agent connection-details with TTL
// - Deduplicate concurrent identical requests
// - Expose simple imperative API and a React hook for components
//
// Notes:
// - No external deps; lightweight singleton store
// - All UI text/comments in English per project standards

import { useEffect, useMemo, useState } from 'react';
import { apiCall } from '../services/api.service';

type Agent = {
  id: number;
  name?: string;
  dbtype?: string;
  database_type?: string;
  [key: string]: any;
};

type AgentsListMeta = {
  cacheKey: string;
  fetchedAt: number;
  expiresAt: number;
};

type DetailsEntry = {
  details: any;
  fetchedAt: number;
  expiresAt: number;
};

// TTLs (adjustable)
const AGENTS_LIST_TTL_MS = 2 * 60 * 1000; // 2 minutes
const AGENT_DETAILS_TTL_MS = 90 * 1000;   // 90 seconds

// Internal store state
const state = {
  agentsById: new Map<number, Agent>(),
  orderedAgentIds: [] as number[],
  listMeta: null as AgentsListMeta | null,
  detailsCache: new Map<number, DetailsEntry>(),
  inFlight: new Map<string, Promise<any>>(),
  subscribers: new Set<() => void>(),
  loadingList: false,
  errorList: null as string | null,
};

function notify() {
  state.subscribers.forEach((cb) => {
    try { cb(); } catch {}
  });
}

function now() {
  return Date.now();
}

/**
 * @deprecated OBSOLETE - Do NOT use localStorage directly
 * 
 * This function is OBSOLETE and should NOT be used.
 * localStorage is NOT a source of truth - AuthContext is the source of truth.
 * 
 * This function is kept for backward compatibility but will return empty values.
 * Always use React hooks that get auth context from AuthContext through useAuth() hook.
 * 
 * @returns {{email?: string, orgId?: number, role?: string}} - Always returns empty object
 */
function getAuthContext(): { email?: string; orgId?: number; role?: string } {
  console.warn("[AgentsRegistry] OBSOLETE: getAuthContext() reads from localStorage which is NOT a source of truth. Use React hooks with AuthContext instead.");
  
  // Return empty - localStorage should NOT be used as source of truth
  return {};
}

function computeListCacheKey(email?: string, orgId?: number, role?: string): string {
  return `${email || 'unknown'}|${orgId || 0}|${role || 'user'}`;
}

function getListEndpoint(email?: string, role?: string, orgId?: number): string {
  if (!email) {
    console.error('[AgentsRegistry] getListEndpoint called without email - cannot build endpoint');
    throw new Error('Email is required to load agents');
  }
  const isSuperUser = role === 'super_user';
  
  // For super_user, use endpoint that returns ALL agents (no org filter)
  // For others, use assigned endpoint
  const baseEndpoint = isSuperUser
    ? `users/${encodeURIComponent(email)}/agents`
    : `users/${encodeURIComponent(email)}/agents/assigned`;

  const endpoint = (!isSuperUser && orgId) ? `${baseEndpoint}?org_id=${orgId}` : baseEndpoint;

  console.log(`[AgentsRegistry] getListEndpoint: role=${role}, isSuperUser=${isSuperUser}, orgId=${orgId}, endpoint=${endpoint}`);

  return endpoint;
}

function getDetailsEndpoint(agentId: number): string {
  return `agents/${agentId}/connection-details`;
}

function isListFresh(): boolean {
  if (!state.listMeta) return false;
  return state.listMeta.expiresAt > now();
}

function isDetailsFresh(agentId: number): boolean {
  const entry = state.detailsCache.get(agentId);
  return !!entry && entry.expiresAt > now();
}

function setAgentsList(agents: Agent[], email?: string, orgId?: number, role?: string) {
  state.agentsById.clear();
  state.orderedAgentIds = [];
  for (const a of agents) {
    if (a && typeof a.id === 'number') {
      state.agentsById.set(a.id, a);
      state.orderedAgentIds.push(a.id);
    }
  }
  state.listMeta = {
    cacheKey: computeListCacheKey(email, orgId, role),
    fetchedAt: now(),
    expiresAt: now() + AGENTS_LIST_TTL_MS,
  };
}

async function dedupedFetch(key: string, fn: () => Promise<any>): Promise<any> {
  const existing = state.inFlight.get(key);
  if (existing) return existing;
  const p = fn().finally(() => {
    state.inFlight.delete(key);
  });
  state.inFlight.set(key, p);
  return p;
}

export const AgentsRegistry = {
  subscribe(cb: () => void) {
    state.subscribers.add(cb);
    return () => state.subscribers.delete(cb);
  },

  getState() {
    return {
      agents: state.orderedAgentIds.map((id) => state.agentsById.get(id)).filter(Boolean) as Agent[],
      loading: state.loadingList,
      error: state.errorList,
      listMeta: state.listMeta,
    };
  },

  /**
   * Initialize agents from login response (optimization: avoid extra request)
   * This is called automatically by AuthContext when agents are received in login response
   */
  initializeFromLogin(agents: Agent[], email: string, orgId?: number, role?: string): void {
    if (!agents || agents.length === 0) {
      console.log('[AgentsRegistry] No agents provided in login response');
      return;
    }

    const cacheKey = computeListCacheKey(email, orgId, role);
    const now = Date.now();
    
    // Set agents in registry (reuse existing setAgentsList function)
    setAgentsList(agents, email, orgId, role);
    
    console.log(`✅ [AgentsRegistry] Initialized ${agents.length} agents from login response (cache key: ${cacheKey})`);
  },

  async loadAgents(options?: { email?: string; orgId?: number; role?: string; force?: boolean }): Promise<Agent[]> {
    const { email, orgId, role, force = false } = options || {};
    
    if (!email) {
      console.error('[AgentsRegistry] loadAgents called without email - cannot load agents');
      state.errorList = 'Email is required to load agents';
      state.loadingList = false;
      notify();
      return [];
    }

    const desiredKey = computeListCacheKey(email, orgId, role);
    const currentKey = state.listMeta?.cacheKey;
    const cacheKeyChanged = desiredKey !== currentKey;

    // Check if we already have agents loaded from login (same cache key and fresh)
    if (!force && !cacheKeyChanged && isListFresh()) {
      const current = this.getState().agents;
      if (current.length > 0) {
        console.log(`[AgentsRegistry] Using cached agents from login (${current.length} agents)`);
        return current;
      }
    }

    state.loadingList = true;
    state.errorList = null;
    notify();

    const endpoint = getListEndpoint(email, role, orgId);
    const reqKey = `GET|${endpoint}`;
    
    console.log(`[AgentsRegistry] loadAgents: email=${email}, role=${role}, orgId=${orgId}, endpoint=${endpoint}`);
    
    try {
      const res = await dedupedFetch(reqKey, async () => apiCall(endpoint, 'GET'));
      const list = Array.isArray(res) ? res : [];
      console.log(`[AgentsRegistry] loadAgents: received ${list.length} agents`);
      setAgentsList(list, email, orgId, role);
      state.loadingList = false;
      notify();
      return this.getState().agents;
    } catch (e: any) {
      state.loadingList = false;
      state.errorList = e?.message || 'Failed to load agents';
      notify();
      return [];
    }
  },

  getAgentById(id: number): Agent | undefined {
    return state.agentsById.get(id);
  },

  async getConnectionDetails(agentId: number, force = false): Promise<any> {
    if (!force && isDetailsFresh(agentId)) {
      return state.detailsCache.get(agentId)?.details;
    }
    const endpoint = getDetailsEndpoint(agentId);
    const reqKey = `GET|${endpoint}`;
    const details = await dedupedFetch(reqKey, async () => apiCall(endpoint, 'GET'));
    state.detailsCache.set(agentId, {
      details,
      fetchedAt: now(),
      expiresAt: now() + AGENT_DETAILS_TTL_MS,
    });
    // Optionally enrich base agent with dbtype if present
    const a = details?.agent || details;
    if (a && a.dbtype && state.agentsById.has(agentId)) {
      const merged = { ...state.agentsById.get(agentId), dbtype: String(a.dbtype) };
      state.agentsById.set(agentId, merged as Agent);
    }
    notify();
    return details;
  },

  async prefetchDetails(agentIds: number[], concurrency = 3): Promise<void> {
    const queue = [...new Set(agentIds)].filter((id) => !isDetailsFresh(id));
    let idx = 0;
    const workers = Array.from({ length: Math.min(concurrency, queue.length) }, async () => {
      while (idx < queue.length) {
        const id = queue[idx++];
        try { await this.getConnectionDetails(id); } catch {}
      }
    });
    await Promise.all(workers);
  },

  /**
   * Invalidate agents list cache (force refresh on next loadAgents call)
   * Use this after creating, updating, or deleting agents
   */
  invalidateList() {
    state.listMeta = null;
    state.agentsById.clear();
    state.orderedAgentIds = [];
    notify();
  },

  /**
   * Invalidate connection details cache for a specific agent
   * Use this after updating an agent's connection details
   */
  invalidateDetails(agentId: number) {
    state.detailsCache.delete(agentId);
    // Also remove from agentsById if we want to force reload
    if (state.agentsById.has(agentId)) {
      const agent = state.agentsById.get(agentId);
      delete agent?.dbtype;
      delete agent?.database_type;
    }
    notify();
  },

  /**
   * Get complete agent data (agent + RAG + connections + metadata)
   * Uses new backend endpoint /agents/{id}/complete
   */
  async getCompleteAgent(agentId: number, forceRefresh = false): Promise<any> {
    // Check cache first - look for complete agent response (has rag_configuration or connection_metadata)
    if (!forceRefresh) {
      const cached = state.detailsCache.get(agentId);
      if (cached && cached.details) {
        // Check if it's a complete agent response (has rag_configuration or connection_metadata)
        if (cached.details.rag_configuration !== undefined || cached.details.connection_metadata) {
          return cached.details;
        }
      }
    }

    // Deduplicate concurrent requests
    const cacheKey = `complete:${agentId}`;
    if (state.inFlight.has(cacheKey)) {
      return state.inFlight.get(cacheKey)!;
    }

    // Start fetch
    const promise = (async () => {
      try {
        const data = await apiCall(`agents/${agentId}/complete`);
        
        // Cache the complete response
        state.detailsCache.set(agentId, {
          details: data,
          fetchedAt: now(),
          expiresAt: now() + AGENT_DETAILS_TTL_MS,
        });
        
        // Also update agentsById if agent exists in list
        if (state.agentsById.has(agentId) && data.agent) {
          const agent = state.agentsById.get(agentId);
          Object.assign(agent, data.agent);
          if (data.connection_metadata?.dbtype) {
            agent.dbtype = data.connection_metadata.dbtype;
            agent.database_type = data.connection_metadata.dbtype;
          }
        }
        
        notify();
        return data;
      } catch (error: any) {
        throw error;
      } finally {
        state.inFlight.delete(cacheKey);
      }
    })();

    state.inFlight.set(cacheKey, promise);
    return promise;
  },

  /**
   * Clear all cache (list + all details)
   * Use this on logout or major context changes
   */
  clearCache() {
    state.listMeta = null;
    state.agentsById.clear();
    state.orderedAgentIds = [];
    state.detailsCache.clear();
    state.inFlight.clear();
    notify();
  },
};

export function useAgents(options?: { email?: string; orgId?: number; role?: string }) {
  const [snap, setSnap] = useState(AgentsRegistry.getState());
  useEffect(() => {
    return AgentsRegistry.subscribe(() => setSnap(AgentsRegistry.getState()));
  }, []);
  
  // Auto-load agents ONLY if:
  // 1. Email is provided
  // 2. Agents are NOT already loaded from login (cache key matches)
  // 3. Cache is NOT fresh (expired or different user/org/role)
  useEffect(() => {
    if (!options?.email) return;
    
    const currentState = AgentsRegistry.getState();
    const desiredKey = computeListCacheKey(options.email, options.orgId, options.role);
    const currentKey = currentState.listMeta?.cacheKey;
    const hasAgents = currentState.agents.length > 0;
    const cacheMatches = desiredKey === currentKey;
    const cacheFresh = currentState.listMeta && currentState.listMeta.expiresAt > Date.now();
    
    // If agents already loaded from login and cache is fresh, skip auto-load
    if (hasAgents && cacheMatches && cacheFresh) {
      console.log(`[useAgents] Using agents from login (${currentState.agents.length} agents), skipping auto-load`);
      return;
    }
    
    // Only load if we don't have agents or cache doesn't match
    if (!hasAgents || !cacheMatches) {
      console.log(`[useAgents] Auto-loading agents for ${options.email} (role: ${options.role}, orgId: ${options.orgId})`);
      AgentsRegistry.loadAgents({
        email: options.email,
        orgId: options.orgId,
        role: options.role,
        force: false,
      }).catch((err) => {
        console.error('[useAgents] Error loading agents:', err);
      });
    }
  }, [options?.email, options?.orgId, options?.role]);
  
  return useMemo(() => ({
    agents: snap.agents,
    loading: snap.loading,
    error: snap.error,
    refresh: (force = false) => {
      if (!options?.email) {
        console.warn('[useAgents] refresh called but no email provided in options');
        return Promise.resolve([]);
      }
      return AgentsRegistry.loadAgents({
        email: options.email,
        orgId: options.orgId,
        role: options.role,
        force,
      });
    },
    getDetails: (agentId: number, force = false) => AgentsRegistry.getConnectionDetails(agentId, force),
    getCompleteAgent: (agentId: number, force = false) => AgentsRegistry.getCompleteAgent(agentId, force),
  }), [snap, options?.email, options?.orgId, options?.role]);
}


